import { Injectable } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import { TranslocoService } from '@ngneat/transloco';
import { Preferences } from '@capacitor/preferences';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import moment from 'moment';
import 'moment/locale/fr';
import { startWith, take } from 'rxjs/operators';
import { concat, from, Observable } from 'rxjs';

export interface Language {
  code: string;
  name: string;
  flag: string;
  enabled?: boolean; // Control visibility in UI while keeping translation support
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly LANGUAGE_KEY = 'selected_language';

  public readonly languages: Language[] = [
    { code: 'en', name: 'English (EN)', flag: '🇺🇸', enabled: true },
    { code: 'fr', name: 'Français (FR)', flag: '🇫🇷', enabled: true }
  ];

  constructor(private readonly translocoService: TranslocoService) {}

  /**
   * Register locale data for all supported languages
   */
  private registerAllLocales(): void {
    registerLocaleData(localeEn, 'en');
    registerLocaleData(localeFr, 'fr');
  }

  initializeLanguage(multiLanguageSupport: boolean) {
    this.registerAllLocales();

    if (!multiLanguageSupport) {
      this.setLanguage('en');
      return;
    }

    Preferences.get({
      key: this.LANGUAGE_KEY
    })
      .then(({ value: savedLanguage }) => {
        this.setLanguage(savedLanguage || 'en');
      })
      .catch(() => {
        this.setLanguage('en');
      });
  }

  public setLanguage(languageCode: string) {
    const isValidLanguage = this.languages.some(
      ({ code }) => code === languageCode
    );
    if (!isValidLanguage) {
      console.warn('Language not found:', languageCode);
      return;
    }

    concat([
      this.translocoService.load(languageCode),
      from(this.saveLanguage(languageCode))
    ])
      .pipe(take(1))
      .subscribe(() => {
        moment.locale(languageCode);
        this.translocoService.setActiveLang(languageCode);
      });
  }

  private async saveLanguage(languageCode: string): Promise<void> {
    try {
      await Preferences.set({ key: this.LANGUAGE_KEY, value: languageCode });
    } catch (error) {
      console.warn('Error saving language preference:', error);
    }
  }

  public getCurrentLanguage(): string {
    return this.translocoService.getActiveLang();
  }

  public getCurrentLanguageName(): string {
    const currentLang = this.getCurrentLanguage();
    return (
      this.languages.find((lang) => lang.code === currentLang)?.name ||
      'English'
    );
  }

  public get getCurrentLocale$(): Observable<string> {
    return this.translocoService.langChanges$.pipe(
      startWith(this.getCurrentLanguage())
    );
  }
}
